import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import posthog from 'posthog-js';
import { UserBlockingError } from '@hookmyapp/observability/errors';
import { sanitizeReturnTo } from '@/lib/sanitize-return-to';

const API_URL = import.meta.env.VITE_API_URL ?? '';

export interface SessionUser {
  id: string;
  workosId: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  // AIT-639 — server-derived flag from /auth/me (user row < 30 min old);
  // gates the ChatGPT Ads registration ping. Optional so the switch-org
  // projection and test fixtures stay valid.
  isFreshSignup?: boolean;
  cliLastSeenAt: number | null;
  welcomeModalDismissedAt: number | null;
}

export interface SessionState {
  user: SessionUser | null;
  organizationId: string | null;
  role: 'admin' | 'member' | null;
  isLoading: boolean;
}

export interface SessionApi extends SessionState {
  refresh: () => Promise<void>;
  signIn: (opts?: { returnTo?: string }) => void;
  signOut: () => Promise<void>;
  switchOrganization: (organizationId: string) => Promise<void>;
}

const SessionContext = createContext<SessionApi | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>({
    user: null,
    organizationId: null,
    role: null,
    isLoading: true,
  });

  const refresh = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/auth/me`, { credentials: 'include' });
      if (!res.ok) {
        setState({ user: null, organizationId: null, role: null, isLoading: false });
        return;
      }
      const body = (await res.json()) as {
        user: SessionUser;
        organizationId: string | null;
        role: 'admin' | 'member' | null;
      };
      setState({
        user: body.user,
        organizationId: body.organizationId,
        role: body.role,
        isLoading: false,
      });
    } catch {
      setState({ user: null, organizationId: null, role: null, isLoading: false });
    }
  }, []);

  // /auth/me is an external bootstrap call whose result populates state
  // exactly once on mount. Calling `refresh()` inside an async IIFE keeps
  // setState off the synchronous effect body so the set-state-in-effect
  // analyzer doesn't flag it (it tracks direct setState calls).
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      await refresh();
      // cancellation only matters if the caller unmounts mid-fetch; refresh()
      // already setStates internally, so we don't double-set here.
      void cancelled;
    })();
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  useEffect(() => {
    // Initialize to 0 so the first focus/visibilitychange after mount is
    // allowed through immediately. Initial-fetch on mount is handled by the
    // separate `useEffect` that calls refresh() once; debouncing only matters
    // for the *next* focus event onwards. Setting lastRefresh = Date.now()
    // here would block the first focus for 30s after mount.
    let lastRefresh = 0;
    const REFRESH_DEBOUNCE_MS = 30_000;
    const maybeRefresh = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastRefresh < REFRESH_DEBOUNCE_MS) return;
      lastRefresh = Date.now();
      void refresh();
    };
    document.addEventListener('visibilitychange', maybeRefresh);
    window.addEventListener('focus', maybeRefresh);
    return () => {
      document.removeEventListener('visibilitychange', maybeRefresh);
      window.removeEventListener('focus', maybeRefresh);
    };
  }, [refresh]);

  const signIn = useCallback((opts?: { returnTo?: string }) => {
    const candidate = opts?.returnTo ?? `${window.location.pathname}${window.location.search}`;
    const returnTo = sanitizeReturnTo(candidate);
    window.location.assign(`${API_URL}/auth/login?returnTo=${encodeURIComponent(returnTo)}`);
  }, []);

  const signOut = useCallback(async () => {
    // Reset PostHog distinct_id BEFORE redirect so the next user's identify()
    // doesn't get "Refused to merge already identified user" (PostHog refuses
    // to merge two identified ids — must reset to anonymous between users).
    posthog.reset();
    try {
      const res = await fetch(`${API_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        const body = await res.json().catch(() => ({}));
        if (body?.logoutUrl) {
          window.location.assign(body.logoutUrl);
          return;
        }
      }
    } catch {
      /* fall through to local clear */
    }
    setState({ user: null, organizationId: null, role: null, isLoading: false });
    window.location.assign('/');
  }, []);

  const switchOrganization = useCallback(async (organizationId: string) => {
    const res = await fetch(`${API_URL}/auth/switch-organization`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ organizationId }),
    });
    if (!res.ok) {
      throw new UserBlockingError({
        message: 'switch-organization failed',
        code: 'SWITCH_ORGANIZATION_FAILED',
        details: { status: res.status },
        userMessage: 'Could not switch organization. Please try again.',
      });
    }
    const body = (await res.json()) as {
      user: SessionUser;
      organizationId: string;
      role: 'admin' | 'member' | null;
    };
    setState((prev) => ({
      ...prev,
      user: body.user,
      organizationId: body.organizationId,
      role: body.role,
    }));
  }, []);

  return (
    <SessionContext.Provider
      value={{ ...state, refresh, signIn, signOut, switchOrganization }}
    >
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): SessionApi {
  const ctx = useContext(SessionContext);
  if (!ctx) {
    throw new UserBlockingError({
      message: 'useSession must be used inside SessionProvider',
      code: 'SESSION_PROVIDER_MISSING',
    });
  }
  return ctx;
}
